/**
 * Owner layout overrides — remote/bundled authoring data layered on shipped defs.
 *
 * Runtime defaults live in worlds/*.ts. This document only stores transform
 * overrides (x/y/scale/rotation/depth/flip/enabled) per space + layout profile.
 */

import type { LayoutProfile } from '@/world-prototype/editor/layoutProfile';
import type { EditableProp } from '@/world-prototype/editor/types';

export const LAYOUT_OVERRIDES_VERSION = 1 as const;

/** One object's authored transform for a single layout profile. */
export interface ObjectLayoutOverride {
  x?: number;
  y?: number;
  scale?: number;
  heightFrac?: number;
  rotationDeg?: number;
  depth?: number;
  flipX?: boolean;
  enabled?: boolean;
}

export type ProfileOverrides = Record<string, ObjectLayoutOverride>;

export type SpaceOverrides = Partial<
  Record<LayoutProfile, ProfileOverrides>
>;

export interface LayoutOverridesDocument {
  version: typeof LAYOUT_OVERRIDES_VERSION;
  updatedAt?: string;
  spaces: Record<string, SpaceOverrides>;
}

export function emptyLayoutOverrides(): LayoutOverridesDocument {
  return { version: LAYOUT_OVERRIDES_VERSION, spaces: {} };
}

export function isLayoutOverridesDocument(
  value: unknown,
): value is LayoutOverridesDocument {
  if (!value || typeof value !== 'object') return false;
  const doc = value as LayoutOverridesDocument;
  return doc.version === LAYOUT_OVERRIDES_VERSION && typeof doc.spaces === 'object';
}

/** Merge `patch` into `base` (patch wins per object field). */
export function mergeLayoutOverrides(
  base: LayoutOverridesDocument,
  patch: LayoutOverridesDocument,
): LayoutOverridesDocument {
  const spaces: Record<string, SpaceOverrides> = { ...base.spaces };
  for (const [spaceId, spacePatch] of Object.entries(patch.spaces ?? {})) {
    const prev = spaces[spaceId] ?? {};
    const next: SpaceOverrides = { ...prev };
    for (const profile of ['desktop', 'tablet', 'mobile'] as const) {
      const profilePatch = spacePatch[profile];
      if (!profilePatch) continue;
      const prevProfile = prev[profile] ?? {};
      const mergedProfile: ProfileOverrides = { ...prevProfile };
      for (const [objectId, objectPatch] of Object.entries(profilePatch)) {
        mergedProfile[objectId] = {
          ...(prevProfile[objectId] ?? {}),
          ...objectPatch,
        };
      }
      next[profile] = mergedProfile;
    }
    spaces[spaceId] = next;
  }
  return {
    version: LAYOUT_OVERRIDES_VERSION,
    updatedAt: patch.updatedAt ?? base.updatedAt,
    spaces,
  };
}

/** Build overrides for one space+profile from live editor props. */
export function overridesFromEditableProps(
  spaceId: string,
  profile: LayoutProfile,
  props: readonly EditableProp[],
): LayoutOverridesDocument {
  const profileMap: ProfileOverrides = {};
  for (const prop of props) {
    profileMap[prop.id] = {
      x: prop.x,
      y: prop.y,
      scale: prop.scale,
      ...(prop.heightFrac != null ? { heightFrac: prop.heightFrac } : {}),
      rotationDeg: prop.rotationDeg,
      depth: prop.depth,
      flipX: prop.flipX,
      enabled: prop.enabled,
    };
  }
  return {
    version: LAYOUT_OVERRIDES_VERSION,
    updatedAt: new Date().toISOString(),
    spaces: {
      [spaceId]: {
        [profile]: profileMap,
      },
    },
  };
}

/**
 * Patch a declarative def so `withResolvedLayout` sees overridden transforms
 * for the active profile. Does not mutate the original.
 */
export function applyObjectLayoutOverride<T extends {
  id?: string;
  enabled?: boolean;
  x?: number;
  y?: number;
  rotationDeg?: number;
  depth?: number;
  flipX?: boolean;
  layouts?: Partial<
    Record<
      LayoutProfile,
      {
        x?: number;
        y?: number;
        scale?: number;
        heightFrac?: number;
        rotationDeg?: number;
        depth?: number;
        flipX?: boolean;
      }
    >
  >;
}>(
  def: T,
  spaceId: string,
  profile: LayoutProfile,
  doc: LayoutOverridesDocument,
): T {
  const id = def.id;
  if (!id) return def;
  const override = doc.spaces[spaceId]?.[profile]?.[id];
  if (!override) return def;

  const layouts = def.layouts
    ? {
        ...def.layouts,
        [profile]: {
          ...def.layouts[profile],
          ...(override.x != null ? { x: override.x } : {}),
          ...(override.y != null ? { y: override.y } : {}),
          ...(override.scale != null ? { scale: override.scale } : {}),
          ...(override.heightFrac != null
            ? { heightFrac: override.heightFrac }
            : {}),
          ...(override.rotationDeg != null
            ? { rotationDeg: override.rotationDeg }
            : {}),
          ...(override.depth != null ? { depth: override.depth } : {}),
          ...(override.flipX != null ? { flipX: override.flipX } : {}),
        },
      }
    : def.layouts;

  return {
    ...def,
    ...(layouts ? { layouts } : {}),
    ...(override.x != null ? { x: override.x } : {}),
    ...(override.y != null ? { y: override.y } : {}),
    ...(override.rotationDeg != null ? { rotationDeg: override.rotationDeg } : {}),
    ...(override.depth != null ? { depth: override.depth } : {}),
    ...(override.flipX != null ? { flipX: override.flipX } : {}),
    ...(override.enabled != null ? { enabled: override.enabled } : {}),
  };
}
